import { useState } from "react";
import { useApp } from "@/store/AppStore";
import { primaryButtonClass } from "@/components/base/fieldStyles";
import { formatNumber } from "@/utils/format";
import { BACKUP_TABLES } from "@/utils/jsonBackup";
import type { AppData } from "@/types";

const countFor = (data: AppData, key: (typeof BACKUP_TABLES)[number]["key"]): number => {
  if (key === "settings") return 1;
  const value = (data as unknown as Record<string, unknown>)[key];
  return Array.isArray(value) ? value.length : 0;
};

const ExportPanel = () => {
  const { data, exportBackup } = useApp();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ records: number; modules: number } | null>(null);

  const run = () => {
    setBusy(true);
    setError("");
    setMessage("");
    setResult(null);
    try {
      const payload = exportBackup();
      const records = Object.values(payload.counts).reduce((a, b) => a + b, 0);
      const modules = Object.values(payload.counts).filter((n) => n > 0).length;
      setResult({ records, modules });
      setMessage("Respaldo descargado correctamente.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo generar el respaldo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass-panel rounded-lg p-4 md:p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-md bg-accent-500/20 text-accent-200 flex items-center justify-center">
          <i className="ri-download-2-line" />
        </span>
        <h3 className="editable-title text-base font-bold">Exportar respaldo (.json)</h3>
      </div>
      <p className="text-[11px] text-foreground-500 mb-4">
        Descarga toda la base de datos en un solo archivo JSON (con las categorías separadas). Ese
        mismo archivo lo puedes volver a cargar aquí para restaurar lo que necesites.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {BACKUP_TABLES.map((t) => (
          <div
            key={t.key}
            className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2"
          >
            <p className="text-[10px] text-foreground-500 flex items-center gap-1 truncate">
              <i className={t.icon} /> {t.label}
            </p>
            <p className="text-sm font-bold text-foreground-950">
              {formatNumber(countFor(data, t.key))}
            </p>
          </div>
        ))}
      </div>

      <button onClick={run} disabled={busy} className={primaryButtonClass}>
        <i className="ri-download-2-line" /> {busy ? "Generando..." : "Descargar respaldo (JSON)"}
      </button>

      {message && !error && (
        <div className="mt-3 rounded-md border border-accent-500/30 bg-accent-500/5 px-3 py-2 animate-fade-up">
          <p className="text-[11px] text-accent-200 flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line" /> {message}
          </p>
          {result && (
            <p className="text-[10px] text-foreground-600 mt-0.5">
              {formatNumber(result.records)} registro(s) en {formatNumber(result.modules)} módulo(s).
            </p>
          )}
        </div>
      )}
      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5 animate-fade-up">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </div>
  );
};

export default ExportPanel;